import { animate } from '@theme/motion';
import { moveIndicator } from '@theme/active-indicator';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// A panel counts as in view once it fills this share of the visible area, or once
// this much of the panel itself is on screen (so short panels like "More" count).
const MIN_SHARE_OF_VIEW = 0.35;
const MIN_SHARE_OF_PANEL = 0.8;

// Browsers without `scrollend` end a click's scroll on this timer instead. Where
// the event exists a timer would cut long smooth scrolls short.
const SCROLL_SETTLE_MS = 1200;
const HAS_SCROLLEND = 'onscrollend' in window;

class CategoryMenu extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.category-menu__rail');
    this.content = this.querySelector('.category-menu__content');
    // Category blocks render their rail item beside their panel; gather them into the rail.
    this.content.querySelectorAll('.category-rail__item').forEach((item) => this.rail.append(item));
    this.railItems = Array.from(this.querySelectorAll('[data-category-target]'));
    this.highlight = this.rail.querySelector('.category-menu__rail-highlight');
    this.sheen = this.rail.querySelector('.category-menu__rail-sheen');
    this.panels = this.railItems.map((item) => this.querySelector(`#${item.dataset.categoryTarget}`));
    if (!this.railItems.length) return;

    // Inside the drawer the content column scrolls; on the standalone page the window does.
    const overflow = getComputedStyle(this.content).overflowY;
    this.scroller = overflow === 'auto' || overflow === 'scroll' ? this.content : window;

    this.railItems.forEach((item, index) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToPanel(index);
      });
    });

    // Only the shopper's own scrolling drives the spy (and can combine items).
    // A click's smooth scroll is ignored, and its single item stays put after.
    this.scrollHandler = () => {
      if (this.frame || this.clickScroll) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActive({ fromScroll: true });
      });
    };
    this.scroller.addEventListener('scroll', this.scrollHandler, { passive: true });

    this.scrollEndHandler = () => {
      if (this.clickScroll) this.settleClick();
    };
    this.scroller.addEventListener('scrollend', this.scrollEndHandler);

    if (window.Shopify?.designMode) {
      this.blockSelectHandler = (event) => {
        const index = this.panels.findIndex((panel) => panel?.contains(event.target));
        if (index !== -1) this.scrollToPanel(index);
      };
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
    }

    // Rail width changes at the breakpoint; re-seat the highlight without sliding.
    this.resizeObserver = new ResizeObserver(() => {
      moveIndicator(this.highlight, this.railItems[this.activeIndex], { instant: true });
    });
    this.resizeObserver.observe(this.rail);

    this.updateActive();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.scroller?.removeEventListener('scroll', this.scrollHandler);
    this.scroller?.removeEventListener('scrollend', this.scrollEndHandler);
    if (this.frame) cancelAnimationFrame(this.frame);
    clearTimeout(this.clickScroll?.timer);
    if (this.blockSelectHandler) document.removeEventListener('shopify:block:select', this.blockSelectHandler);
  }

  scrollToPanel(index) {
    const panel = this.panels[index];
    if (!panel) return;
    this.setActive(index);
    this.playSheen();

    clearTimeout(this.clickScroll?.timer);
    this.clickScroll = { index, timer: HAS_SCROLLEND ? null : setTimeout(() => this.settleClick(), SCROLL_SETTLE_MS) };

    // Scroll the column only — scrollIntoView would also nudge the drawer's ancestors.
    const scrollingWindow = this.scroller === window;
    const current = scrollingWindow ? window.scrollY : this.content.scrollTop;
    const maxScroll = scrollingWindow
      ? document.documentElement.scrollHeight - window.innerHeight
      : this.content.scrollHeight - this.content.clientHeight;
    const wanted = current + panel.getBoundingClientRect().top - this.viewBounds.top;
    const target = Math.max(0, Math.min(wanted, maxScroll));

    // Already there (e.g. the last panel at the bottom): no scrollend will come.
    if (Math.abs(target - current) < 2) {
      this.settleClick();
      return;
    }
    (scrollingWindow ? window : this.content).scrollTo({ top: target, behavior: 'smooth' });
  }

  settleClick() {
    if (!this.clickScroll) return;
    const { index, timer } = this.clickScroll;
    clearTimeout(timer);
    this.clickScroll = null;

    // Baseline for the spy, so panels already on screen don't count as "entered"
    // on the shopper's next scroll.
    this.visibleIndexes = this.visiblePanels().map((entry) => entry.index);
    this.lastPosition = this.scrollPosition;

    // Short or final panels can't reach the top, so neighbours share the view.
    // Point at the one that was clicked.
    const othersInView = this.visiblePanels().some((entry) => entry.index !== index);
    if (othersInView) this.hintPanel(this.panels[index]);
  }

  // A soft accent glow that fades out over a panel that just became active.
  hintPanel(panel) {
    let glow = panel.querySelector(':scope > .category-panel__hint');
    if (!glow) {
      glow = document.createElement('span');
      glow.className = 'category-panel__hint';
      glow.setAttribute('aria-hidden', 'true');
      panel.prepend(glow);
    }

    // Only the active panel glows: fade out the previous one if it's still lit.
    if (this.glow && this.glow !== glow) {
      this.glowAnimation?.stop();
      animate(this.glow, { opacity: 0 }, { duration: 0.2 });
    }
    this.glow = glow;
    this.glowAnimation = animate(
      glow,
      { opacity: [0, 1, 1, 0] },
      { duration: reducedMotion.matches ? 1 : 1.6, times: [0, 0.15, 0.6, 1], ease: 'easeInOut' }
    );
  }

  // The visible band of the scroller. On the standalone page the sticky site
  // header covers the top of the window; inside the drawer nothing does.
  get viewBounds() {
    if (this.scroller !== window) {
      const rect = this.content.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom };
    }
    const header = document.querySelector('.section-header');
    const offset = Math.max(0, header ? header.getBoundingClientRect().bottom : 0);
    this.style.setProperty('--category-menu-offset', `${offset}px`);
    return { top: offset, bottom: window.innerHeight };
  }

  // Every panel meaningfully on screen, with how many px of it show.
  visiblePanels() {
    const { top, bottom } = this.viewBounds;
    const viewHeight = bottom - top;
    const visible = [];
    this.panels.forEach((panel, index) => {
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const shown = Math.min(rect.bottom, bottom) - Math.max(rect.top, top);
      if (shown >= viewHeight * MIN_SHARE_OF_VIEW || shown >= rect.height * MIN_SHARE_OF_PANEL) {
        visible.push({ index, shown });
      }
    });
    return visible;
  }

  get scrollPosition() {
    return this.scroller === window ? window.scrollY : this.content.scrollTop;
  }

  // One active item. At rest it's the topmost panel in view. While the shopper
  // scrolls, a panel that newly enters the view takes over (and glows), so the
  // rail follows what just appeared; otherwise the current one holds until it leaves.
  // The drawer reopening a tab where the shopper left it: the matching item, no slide or glow.
  restoreScroll(top) {
    if (this.scroller === window || !this.railItems.length) return;
    this.content.scrollTop = top;
    this.updateActive({ instant: true });
  }

  updateActive({ fromScroll = false, instant = false } = {}) {
    const visible = this.visiblePanels().map((entry) => entry.index);
    const previous = this.visibleIndexes || [];
    const position = this.scrollPosition;
    const scrollingUp = position < (this.lastPosition ?? position);
    this.visibleIndexes = visible;
    this.lastPosition = position;

    if (!visible.length) {
      // Between panels nothing may qualify; keep what's active.
      if (this.activeIndex === undefined) this.setActive(0);
      return;
    }

    const entered = visible.filter((index) => !previous.includes(index));
    let next;
    if (!fromScroll || this.activeIndex === undefined) {
      next = visible[0];
    } else if (entered.length) {
      // Several at once (a fast flick): the one furthest along the scroll.
      next = scrollingUp ? entered[0] : entered[entered.length - 1];
    } else if (!visible.includes(this.activeIndex)) {
      next = visible.reduce((closest, index) =>
        Math.abs(index - this.activeIndex) < Math.abs(closest - this.activeIndex) ? index : closest
      );
    } else {
      return;
    }

    if (next === this.activeIndex) return;
    this.setActive(next, { instant });
    if (fromScroll && entered.includes(next)) this.hintPanel(this.panels[next]);
  }

  setActive(index, { instant = false } = {}) {
    if (this.activeIndex === index) return;
    this.activeIndex = index;
    this.railItems.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);
      if (isActive) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
    this.panels.forEach((panel, panelIndex) => panel?.classList.toggle('is-active', panelIndex === index));
    moveIndicator(this.highlight, this.railItems[index], { instant });
    this.revealRailItem(this.railItems[index]);
  }

  // Click feedback only — scroll-spy moves stay quiet.
  playSheen() {
    if (!this.sheen || reducedMotion.matches) return;
    animate(this.sheen, { x: ['-100%', '100%'], opacity: [0, 1, 0] }, { duration: 0.7, ease: 'easeOut' });
  }

  // Keeps the active item visible in a long rail without scrolling the page.
  revealRailItem(item) {
    if (!item || this.rail.scrollHeight <= this.rail.clientHeight) return;
    const railRect = this.rail.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top < railRect.top) {
      this.rail.scrollTop -= railRect.top - itemRect.top;
    } else if (itemRect.bottom > railRect.bottom) {
      this.rail.scrollTop += itemRect.bottom - railRect.bottom;
    }
  }
}

if (!customElements.get('category-menu')) customElements.define('category-menu', CategoryMenu);
